'use client';

import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';
import { getDraftUploadUrl, type DraftUploadUrlInput } from '../lib/api/attachments';
import { getAuthSession } from '../lib/authSession';
import { hasDesktopBridge, setPendingCapture, type DesktopBridgeCapturePayload } from '../lib/desktopBridge';
import { MAX_PENDING_CAPTURES, useDesktopCaptureStore, type PendingCapture } from '../stores/desktopCaptureStore';

const MAX_PNG_BYTES = 10 * 1024 * 1024; // 첨부 정책과 동일(10MB)

// useTranslations("components.appLayout") 반환 함수와 호환되는 최소 시그니처.
// helper들이 component scope 밖이라 t를 인자로 받아 localized error를 throw 한다.
type Translator = (key: string, values?: Record<string, string | number>) => string;

type DesktopServiceWorkerNotificationMessage = {
  type: 'AGENTTEAMS_DESKTOP_NOTIFICATION';
  payload?: {
    title?: unknown;
    body?: unknown;
    url?: unknown;
    tag?: unknown;
  };
};

function isDesktopNotificationMessage(value: unknown): value is DesktopServiceWorkerNotificationMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    (value as { type?: unknown }).type === 'AGENTTEAMS_DESKTOP_NOTIFICATION'
  );
}

export function toDesktopNotificationPayload(payload: unknown): Record<string, unknown> | null {
  if (!payload || typeof payload !== 'object') return null;
  const candidate = payload as DesktopServiceWorkerNotificationMessage['payload'];
  if (typeof candidate?.title !== 'string' || candidate.title.trim().length === 0) return null;
  return {
    title: candidate.title,
    body: typeof candidate.body === 'string' ? candidate.body : '',
    url: typeof candidate.url === 'string' ? candidate.url : '/home',
    tag: typeof candidate.tag === 'string' ? candidate.tag : undefined,
  };
}

function dataUrlToBlob(dataUrl: string, t: Translator): Blob {
  const commaIdx = dataUrl.indexOf(',');
  if (commaIdx < 0) throw new Error(t('desktopCaptureInvalidDataUrl'));
  const header = dataUrl.slice(0, commaIdx);
  const body = dataUrl.slice(commaIdx + 1);
  const mimeMatch = header.match(/data:([^;]+);/);
  const mime = mimeMatch?.[1] ?? 'image/png';
  const binary = atob(body);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mime });
}

async function putToPresignedUrl(uploadUrl: string, blob: Blob, t: Translator): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': blob.type || 'image/png' },
    body: blob,
  });
  if (!response.ok) {
    throw new Error(t('desktopCaptureUploadStatusError', { status: response.status }));
  }
}

async function uploadCaptureBlob(
  payload: DesktopBridgeCapturePayload,
  token: string,
  t: Translator,
): Promise<{ key: string; size: number; originalName: string }> {
  const blob = dataUrlToBlob(payload.pngDataUrl, t);
  if (blob.size > MAX_PNG_BYTES) {
    throw new Error(t('desktopCaptureSizeExceeded', { maxMb: MAX_PNG_BYTES / (1024 * 1024) }));
  }
  const originalName = `desktop-capture-${payload.captureId.slice(0, 8)}.png`;
  const input: DraftUploadUrlInput = {
    fileName: originalName,
    contentType: blob.type || 'image/png',
    size: blob.size,
  };
  const { uploadUrl, key } = await getDraftUploadUrl(token, input);
  await putToPresignedUrl(uploadUrl, blob, t);
  return { key, size: blob.size, originalName };
}

/**
 * 데스크탑 앱(`desktop/`)에서 webview로 전송한 캡처를 받아
 *  1. 한도(10건) 체크 — 초과면 토스트 + acknowledge 없이 거부
 *  2. R2 presigned PUT으로 업로드
 *  3. useDesktopCaptureStore에 PendingCapture(uploading → ready/failed)로 보관
 *  4. UI 알림: 업로드 중 sonner.loading, 실패 시 toast.error (성공 시 칩이 자동 갱신)
 *
 * `window.agentteamsBridge`가 없으면(일반 웹 브라우저) 본 컴포넌트는 아무 동작도 하지 않는다.
 */
export function DesktopBridgeProvider(): null {
  const t = useTranslations('components.appLayout');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasDesktopBridge()) return;

    const handle = async (payload: DesktopBridgeCapturePayload): Promise<void> => {
      const captureId = payload.captureId;

      // 한도 사전 체크 — addCapture는 한도면 false 반환.
      const pending: PendingCapture = {
        captureId,
        mode: payload.followUp === true ? 'followUp' : 'new',
        status: 'uploading',
        description: payload.description ?? '',
        rawOnly: payload.rawOnly === true,
        width: payload.width ?? 0,
        height: payload.height ?? 0,
        capturedAt: payload.capturedAt ?? new Date().toISOString(),
        thumbnailDataUrl: payload.pngDataUrl,
      };

      const accepted = useDesktopCaptureStore.getState().addCapture(pending);
      if (!accepted) {
        toast.error(t('desktopCaptureLimitReachedTitle', { max: MAX_PENDING_CAPTURES }), {
          description: t('desktopCaptureLimitReachedBody'),
        });
        // acknowledge는 보내지 않음 — 데스크탑 측이 임시 파일을 이미 정리했지만 사용자에게
        // 거부 사실을 명확히 알리는 게 목적. 캡처 자체는 손실되므로 다시 캡처해야 함.
        return;
      }

      window.agentteamsBridge?.request('capture:acknowledge', { captureId });
      setPendingCapture(payload);

      const toastId = toast.loading(t('desktopCaptureUploading'));

      try {
        const session = getAuthSession();
        if (!session?.token) {
          throw new Error(t('desktopCaptureNeedsLogin'));
        }
        const { key, size, originalName } = await uploadCaptureBlob(payload, session.token, t);
        useDesktopCaptureStore.getState().setStatus(captureId, 'ready', {
          attachmentDraft: {
            key,
            originalName,
            mimeType: 'image/png',
            size,
          },
        });
        toast.dismiss(toastId);
        setPendingCapture(null);
      } catch (err) {
        const message = err instanceof Error ? err.message : t('desktopCaptureUploadFallback');
        useDesktopCaptureStore.getState().setStatus(captureId, 'failed', { errorMessage: message });
        toast.error(t('desktopCaptureUploadFailedTitle'), {
          id: toastId,
          description: message,
        });
      }
    };

    const off = window.agentteamsBridge?.onCapture((payload) => {
      void handle(payload);
    });

    return () => {
      off?.();
    };
  }, [t]);

  // 데스크탑 셸 사이드 메뉴의 설정 버튼 → 홈 화면의 워크스페이스 설정 모달을 연다.
  // 기존 전역 이벤트('openWorkspaceModal')를 재사용해 모달 오픈 상태 소유(AppProviders)와 결합하지 않는다.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!hasDesktopBridge()) return;
    const off = window.agentteamsBridge?.onSettingsOpen?.(() => {
      window.dispatchEvent(new Event('openWorkspaceModal'));
    });
    return () => {
      off?.();
    };
  }, []);

  useEffect(() => {
    if (typeof navigator === 'undefined') return;
    if (!hasDesktopBridge()) return;
    if (!('serviceWorker' in navigator)) return;

    const handleServiceWorkerMessage = (event: MessageEvent): void => {
      if (!isDesktopNotificationMessage(event.data)) return;

      const payload = toDesktopNotificationPayload(event.data.payload);
      const handled = Boolean(payload);
      if (payload) {
        window.agentteamsBridge?.request('notification:show', payload);
      }
      event.ports?.[0]?.postMessage({ handled });
    };

    navigator.serviceWorker.addEventListener('message', handleServiceWorkerMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  return null;
}
